import React, { useState, useMemo, useEffect } from 'react';
import {
  Grid,
  Select,
  InputLabel,
  Typography,
  MenuItem,
  FormControl,
} from '@mui/material';
import StateSelector from '../../Components/StateSelector';
import CountrySelector from '../../Components/CountrySelector';
import { useApiSend, useApiGet } from '../../utils/httpClient';
import ChapterSelector from '../../Components/ChapterSelector';
import ValidatedTextField from '../../Components/ValidatedTextField';
import AlertItem from '../../Components/AlertItem';
import { LoadingButton } from '@mui/lab';
import { createUsers } from '../../endpoints/users';
import {
  nameValidator,
  emailValidator,
  phoneNumberValidator,
} from '../../utils/validators';
import DateTimePicker from '../../Components/DateTimePicker';
import dayjs from 'dayjs';
import { getPostalInfo } from '../../endpoints/lookups';
import { Add } from '@mui/icons-material';

function CreateTextField({
  label,
  value,
  readOnly,
  onChange,
  validator = null,
  error,
  valueRequired = true,
}) {
  return (
    <Grid item xs={6} paddingLeft={2} paddingRight={2} paddingBottom={2}>
      <ValidatedTextField
        validator={validator}
        label={label}
        disabled={readOnly}
        startAdornment={null}
        valueRequired={valueRequired}
        errorOverride={error ? 'This field is required' : null}
        value={value}
        onChange={(isValid, value) => onChange(isValid, value)}
      />
    </Grid>
  );
}

function UserCreate() {
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [cellPhone, setCellPhone] = useState('');
  const [cellPhoneError, setCellPhoneError] = useState(false);
  const [chapterId, setChapterId] = useState('');
  const [chapterError, setChapterError] = useState(false);
  const [address1, setAddress1] = useState('');
  const [address1Error, setAddress1Error] = useState(false);
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState(false);
  const [stateId, setStateId] = useState('');
  const [stateError, setStateError] = useState(false);
  const [stateRequired, setStateRequired] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [delayPostalCode, setDelayPostalCode] = useState('');
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [countryId, setCountryId] = useState('');
  const [countryError, setCountryError] = useState(false);
  const [initiationDate, setInitiationDate] = useState(null);
  const [initiationDateError, setInitiationDateError] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [creatingMember, setCreatingMember] = useState(true);
  const [getPostalInfoActive, setGetPostalInfoActive] = useState(false);

  const { mutate: createMemberCall, isLoading: isCreatingMember } = useApiSend(
    createUsers,
    (data) => {
      setAlertMessage('Member Initiated Successfully');
      setAlertSeverity('success');
      setShowAlert(true);
      resetStates();
      window.location.href = '/user/' + data.message;
    },
    (error) => {
      setAlertMessage(
        error.message
          ? error.message
          : 'Error Initiating Member. Please try again or contact support.'
      );
      setAlertSeverity('error');
      setShowAlert(true);
    }
  );

  const { data: postalInfo } = useApiGet(
    ['postal-info', postalCode, countryId],
    getPostalInfo,
    {
      enabled: getPostalInfoActive,
    },
    { postalCode, countryId }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPostalCode(delayPostalCode);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayPostalCode]);

  useEffect(() => {
    if (postalCode.length > 4 && countryId.length > 0) {
      setGetPostalInfoActive(true);
    } else {
      setGetPostalInfoActive(false);
    }
  }, [postalCode, countryId]);

  useEffect(() => {
    if (postalInfo) {
      if (postalInfo.city) {
        setCity(postalInfo.city);
        if (postalInfo.city.length > 0) {
          setCityError(false);
        }
      }
      if (postalInfo.state_id) {
        setStateId(postalInfo.state_id);
        setStateRequired(true);
        if (postalInfo.state_id) {
          setStateError(false);
        }
      }
    }
  }, [postalInfo]);

  const resetStates = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAddress1('');
    setAddress2('');
    setCity('');
    setStateId(null);
    setPostalCode('');
    setFirstNameError(false);
    setLastNameError(false);
    setEmailError(false);
    setPhoneError(false);
    setAddress1Error(false);
    setCityError(false);
    setStateError(false);
    setPostalCodeError(false);
    setCountryId('');
    setCountryError(false);
    setInitiationDate(null);
    setInitiationDateError(false);
    setCellPhone('');
    setCellPhoneError(false);
  };

  const handleDateChange = (date) => {
    setInitiationDate(date);
    if (date) {
      setInitiationDateError(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    let valid = true;
    // create
    if (firstName === '') {
      setFirstNameError(true);
      valid = false;
    }
    if (lastName === '') {
      setLastNameError(true);
      valid = false;
    }
    if (email === '') {
      setEmailError(true);
      valid = false;
    }
    if (address1 === '') {
      setAddress1Error(true);
      valid = false;
    }
    if (city === '') {
      setCityError(true);
      valid = false;
    }
    if (stateRequired && stateId === null) {
      setStateError(true);
      valid = false;
    }
    if (postalCode === '') {
      setPostalCodeError(true);
      valid = false;
    }

    if (!initiationDate) {
      setInitiationDateError(true);
      valid = false;
    }

    if (valid) {
      // need to modify phone if its not null and not empty string
      let tempPhone = phone;
      if (phone !== '' && phone !== null) {
        tempPhone = tempPhone.replace(/\D/g, '');
      }
      let tempCellPhone = cellPhone;
      if (cellPhone !== '' && cellPhone !== null) {
        tempCellPhone = tempCellPhone.replace(/\D/g, '');
      }
      let data = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: tempPhone,
        mobile_phone: tempCellPhone,
        address1: address1,
        address2: address2,
        city: city,
        state: stateId,
        postal_code: postalCode,
        initiation_date: initiationDate.format('YYYY-MM-DD'),
        chapter_id: chapterId,
        country: countryId,
      };
      createMemberCall(data);
    }
  };

  const setValue = (field, isValid, value) => {
    switch (field) {
      case 'firstName':
        setFirstName(value);
        if (value !== '' || isValid === false) {
          setFirstNameError(false);
        }
        break;
      case 'lastName':
        setLastName(value);
        if (value !== '' || isValid === false) {
          setLastNameError(false);
        }
        break;
      case 'email':
        setEmail(value);
        if (value !== '' || isValid === false) {
          setEmailError(false);
        }
        break;
      case 'phone':
        setPhone(value);
        if (isValid === false && value !== '') {
          setPhoneError(true);
        } else setPhoneError(false);
        break;
      case 'cellPhone':
        setCellPhone(value);
        if (isValid === false && value !== '') {
          setCellPhoneError(true);
        } else setCellPhoneError(false);
        break;
      case 'address1':
        setAddress1(value);
        if (value !== '' || isValid === false) {
          setAddress1Error(false);
        }
        break;
      case 'address2':
        setAddress2(value);
        break;
      case 'city':
        setCity(value);
        if (value !== '' || isValid === false) {
          setCityError(false);
        }
        break;
      case 'state':
        setStateId(value ? value.value : null);
        if (value !== null || isValid === false) {
          setStateError(false);
        }
        break;
      case 'postalCode':
        setDelayPostalCode(value);
        if (value !== '' || isValid === false) {
          setPostalCodeError(false);
        }
        break;
      default:
        break;
    }
  };

  const canEditAddress = useMemo(() => {
    if (
      postalCode &&
      postalCode.length > 0 &&
      countryId &&
      countryId.length > 0
    ) {
      return true;
    }
    return false;
  }, [postalCode, countryId]);

  const dateErrorMessage = initiationDateError ? 'This field is required' : '';

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h4" textAlign="center">
          {creatingMember ? 'Initiate New Member' : 'Create New User'}
        </Typography>
      </Grid>
      <Grid container item paddingTop={2} xs={12}>
        <Grid item xs={12} padding textAlign={'center'} paddingBottom={2}>
          <FormControl size={'small'}>
            <InputLabel id="user-type-label">User Type</InputLabel>
            <Select
              labelId="user-type-label"
              id="user-type-select"
              value={creatingMember ? 'member' : 'user'}
              label="User Type"
              onChange={(event) => {
                setCreatingMember(event.target.value === 'member');
              }}
              sx={{ minWidth: 200, textAlign: 'left' }}
            >
              <MenuItem value={'user'}>Non-Member</MenuItem>
              <MenuItem value={'member'}>Member</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <CreateTextField
          label="First Name"
          value={firstName}
          readOnly={false}
          error={firstNameError}
          validator={nameValidator}
          onChange={(isValid, value) => setValue('firstName', isValid, value)}
        />
        <CreateTextField
          label="Last Name"
          value={lastName}
          readOnly={false}
          error={lastNameError}
          validator={nameValidator}
          onChange={(isValid, value) => setValue('lastName', isValid, value)}
        />
        <CreateTextField
          label="Email"
          value={email}
          validator={emailValidator}
          readOnly={false}
          error={emailError}
          onChange={(isValid, value) => setValue('email', isValid, value)}
        />
        <CreateTextField
          label="Phone"
          value={phone}
          readOnly={false}
          error={phoneError}
          valueRequired={false}
          validator={phoneNumberValidator}
          onChange={(isValid, value) => setValue('phone', isValid, value)}
        />
        <CreateTextField
          label="Cell Phone"
          value={cellPhone}
          readOnly={false}
          error={cellPhoneError}
          valueRequired={false}
          validator={phoneNumberValidator}
          onChange={(isValid, value) => setValue('cellPhone', isValid, value)}
        />
        <Grid item xs={6} />
        <Grid item xs={6} paddingLeft={2} paddingRight={2} paddingBottom={2}>
          <CountrySelector
            handleChange={(value) => {
              setCountryId(value ? value.id : '');
              if (value !== null) {
                setCountryError(false);
              }
            }}
            value={countryId}
            error={countryError}
            errorMessage={countryError ? 'This field is required' : null}
            size={'small'}
            required={true}
          />
        </Grid>
        <CreateTextField
          label="Postal Code"
          value={delayPostalCode}
          readOnly={false}
          error={postalCodeError}
          onChange={(isValid, value) => setValue('postalCode', isValid, value)}
        />
        <CreateTextField
          label="Address 1"
          value={address1}
          readOnly={canEditAddress ? false : true}
          error={address1Error}
          onChange={(isValid, value) => setValue('address1', isValid, value)}
        />

        <CreateTextField
          label="Address 2"
          value={address2}
          valueRequired={false}
          readOnly={canEditAddress ? false : true}
          onChange={(isValid, value) => setValue('address2', isValid, value)}
        />

        <CreateTextField
          label="City"
          value={city}
          readOnly={canEditAddress ? false : true}
          error={cityError}
          onChange={(isValid, value) => setValue('city', isValid, value)}
        />
        <Grid item xs={6} paddingLeft={2} paddingRight={2} paddingBottom={2}>
          <StateSelector
            countryId={countryId}
            error={stateError}
            errorMessage={stateError ? 'This field is required' : null}
            size={'small'}
            disabled={!canEditAddress}
            required={true}
            value={stateId}
            handleChange={(value) => setValue('state', value === null, value)}
          />
        </Grid>
      </Grid>
      {creatingMember && (
        <Grid container item>
          <Grid item xs={6} paddingLeft={2} paddingRight={2} paddingBottom={2}>
            <DateTimePicker
              size="small"
              date={initiationDate}
              label={'Initiation Date'}
              minDate={dayjs('2025-03-01')}
              maxDate={dayjs('2026-03-01')}
              handleDateChange={handleDateChange}
              handleErrorUpdate={setInitiationDateError}
              extraError={initiationDateError}
              extraErrorMessage={dateErrorMessage}
              disableFuture={false}
            />
          </Grid>
          <Grid item xs={6} paddingLeft={2} paddingRight={2} paddingBottom={2}>
            <ChapterSelector
              chapter={chapterId}
              handleChapterSelect={(value) => {
                setChapterId(value ? value : '');
                if (value !== null) {
                  setChapterError(false);
                }
              }}
              error={chapterError}
              errorMessage={chapterError ? 'This field is required' : null}
              size={'small'}
              required={true}
              disabled={creatingMember}
            />
          </Grid>
        </Grid>
      )}
      <Grid
        item
        paddingLeft={2}
        paddingRight={2}
        paddingBottom={2}
        textAlign={'center'}
        xs={12}
      >
        <LoadingButton
          color="primary"
          variant="contained"
          size="medium"
          loading={isCreatingMember}
          disabled={isCreatingMember}
          onClick={handleSave}
          sx={{ fontWeight: 'bold' }}
        >
          <Add style={{ marginRight: 8, fontSize: 16 }} />
          {creatingMember ? 'Initiate' : 'Create'}
        </LoadingButton>
      </Grid>
      <Grid item xs={12}>
        {showAlert && (
          <AlertItem
            severity={alertSeverity}
            description={alertMessage}
            onClose={() => setShowAlert(false)}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default UserCreate;
